<template>
  <div class="units-overview">
    <div class="nav-bar-operations" :style="{ backgroundColor: brand.topbarColor }">
      <img :src="brand.topbarLogo" :alt="brand.displayName" class="logo-button" />
    </div>

    <div class="shell">
      <header class="header">
        <div class="header__left">
          <div class="icon-badge" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22"><path d="M20 8h-3V4H3a1 1 0 0 0-1 1v11h2a3 3 0 0 0 6 0h4a3 3 0 0 0 6 0h2v-5l-2-3ZM7 17.5A1.5 1.5 0 1 1 8.5 16 1.5 1.5 0 0 1 7 17.5Zm10 0A1.5 1.5 0 1 1 18.5 16 1.5 1.5 0 0 1 17 17.5Zm.5-6.5V9.5h2l1.3 1.5Z" fill="currentColor"/></svg>
          </div>
          <div>
            <p class="eyebrow">Panel de unidades</p>
            <h1>Estado de la flota en vivo</h1>
          </div>
        </div>
        <div class="header__right">
          <span class="live-pill" :data-live="wsStatus === 'open'">
            <span class="live-dot"></span>
            {{ wsStatus === 'open' ? 'En vivo' : 'Reconectando…' }}
          </span>
          <button v-if="canConfigure" class="btn btn--ghost" @click="openConfig">Configuración</button>
          <button class="btn btn--ghost" @click="fetchOverview" :disabled="loading">Actualizar</button>
          <button class="btn btn--ghost" @click="$router.back()">Volver</button>
        </div>
      </header>

      <section class="toolbar">
        <div class="board-tabs" role="tablist">
          <button
            class="board-tab"
            role="tab"
            :aria-selected="activeBoard === 'gruas'"
            :class="{ 'board-tab--active': activeBoard === 'gruas' }"
            @click="activeBoard = 'gruas'"
          >
            Grúas <span class="board-tab__count">{{ boardCounts.gruas }}</span>
          </button>
          <button
            class="board-tab"
            role="tab"
            :aria-selected="activeBoard === 'montacargas'"
            :class="{ 'board-tab--active': activeBoard === 'montacargas' }"
            @click="activeBoard = 'montacargas'"
          >
            Montacargas <span class="board-tab__count">{{ boardCounts.montacargas }}</span>
          </button>
        </div>

        <div class="search">
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 4.99L20.49 19Zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14Z" fill="currentColor"/></svg>
          <input
            v-model.trim="searchQuery"
            type="search"
            placeholder="Buscar unidad, placas u operador…"
            aria-label="Buscar unidades"
          />
          <button v-if="searchQuery" class="search__clear" aria-label="Limpiar búsqueda" @click="searchQuery = ''">×</button>
        </div>
        <label class="sort">
          <span>Ordenar por</span>
          <select v-model="sortBy">
            <option value="inactividad">Tiempo sin servicio</option>
            <option value="unidad">Número de unidad</option>
            <option value="operador">Operador</option>
          </select>
        </label>
      </section>

      <section v-if="activeBoard === 'gruas' && categoryOptions.length" class="cat-filters">
        <span class="cat-filters__label">Categoría</span>
        <button
          class="cat-chip"
          :class="{ 'cat-chip--active': !selectedCategories.length }"
          @click="selectedCategories = []"
        >Todas</button>
        <button
          v-for="cat in categoryOptions"
          :key="cat"
          class="cat-chip"
          :class="{ 'cat-chip--active': selectedCategories.includes(cat) }"
          @click="toggleCategory(cat)"
        >{{ cat }}</button>
      </section>

      <section class="board" :aria-busy="loading">
        <div
          v-for="col in columns"
          :key="col.key"
          class="column"
          :class="`column--${col.key}`"
        >
          <div class="column__head">
            <span class="column__dot"></span>
            <h2>{{ col.title }}</h2>
            <span class="column__count">{{ col.units.length }}</span>
          </div>

          <div class="column__body">
            <transition-group name="card" tag="div" class="column__list">
              <article
                v-for="unit in col.units"
                :key="unit.id"
                class="unit-card"
                :class="{
                  'unit-card--alert':
                    ((col.key === 'libre' || col.key === 'fuera') && isStale(unit)) ||
                    (col.key === 'mantenimiento' && isMaintenanceStale(unit)),
                }"
                role="button"
                tabindex="0"
                @click="openUnit(unit)"
                @keyup.enter="openUnit(unit)"
              >
                <div class="unit-card__top">
                  <span class="unit-card__id">{{ unit.unit_short_id }}</span>
                  <span class="unit-card__plates" v-if="unit.unit_plates">{{ unit.unit_plates }}</span>
                </div>

                <div class="unit-card__operator" :title="unit.operator_name || ''">
                  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4 0-8 2-8 5v2h16v-2c0-3-4-5-8-5Z" fill="currentColor"/></svg>
                  <span>{{ unit.operator_name || 'Sin operador asignado' }}</span>
                </div>

                <template v-if="col.key === 'en_servicio'">
                  <div class="unit-card__services">
                    <component
                      :is="svc.note_ident ? 'a' : 'div'"
                      v-for="(svc, i) in unit.services"
                      :key="i"
                      class="service-row"
                      :class="{ 'service-row--link': svc.note_ident }"
                      v-bind="svc.note_ident ? {
                        href: noteUrl(svc.note_ident),
                        target: '_blank',
                        rel: 'noopener',
                        title: 'Abrir OT ' + svc.note_number + ' en otra pestaña',
                      } : {}"
                      @click="svc.note_ident && $event.stopPropagation()"
                    >
                      <span class="service-chip">
                        {{ svc.note_number ? 'OT ' + svc.note_number : 'Montacargas' }}
                      </span>
                      <span class="service-meta" v-if="svc.note_status">{{ svc.note_status }}</span>
                      <span class="service-meta service-meta--time" v-if="svc.start_time || svc.assigned_at">
                        {{ elapsedLabel(svc.start_time || svc.assigned_at) }}
                      </span>
                      <svg v-if="svc.note_ident" class="service-row__ext" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7ZM19 19H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7h-2v7Z" fill="currentColor"/></svg>
                    </component>
                  </div>
                </template>

                <template v-else-if="col.key === 'libre' || col.key === 'fuera'">
                  <div class="unit-card__last" :class="{ 'unit-card__last--alert': isStale(unit) }">
                    <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm4.2 14.2L11 13.4V6h1.8v6.4l4.4 2.6Z" fill="currentColor"/></svg>
                    <span v-if="unit.last_service_at">
                      Último servicio {{ elapsedLabel(unit.last_service_at) }} · {{ fmtDateTime(unit.last_service_at) }}
                    </span>
                    <span v-else>Sin servicios registrados</span>
                  </div>
                </template>

                <template v-else>
                  <div class="unit-card__last" :class="{ 'unit-card__last--alert': isMaintenanceStale(unit) }">
                    <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm4.2 14.2L11 13.4V6h1.8v6.4l4.4 2.6Z" fill="currentColor"/></svg>
                    <span v-if="unit.maintenance_since">
                      En mantenimiento {{ elapsedLabel(unit.maintenance_since) }} · {{ fmtDateTime(unit.maintenance_since) }}
                    </span>
                    <span v-else>{{ unit.unit_status }}</span>
                  </div>
                </template>
              </article>
            </transition-group>

            <div v-if="!loading && !col.units.length" class="column__empty">
              Sin unidades
            </div>
          </div>
        </div>
      </section>

      <p v-if="error" class="error-note">{{ error }}</p>
    </div>

    <!-- Unit detail modal ("uo-" prefix avoids Bootstrap's global .modal styles) -->
    <transition name="uo-modal">
      <div v-if="selectedUnit" class="uo-modal-backdrop" @click.self="closeUnit">
        <div class="uo-modal" role="dialog" aria-modal="true" :aria-label="'Detalle de unidad ' + selectedUnit.unit_short_id">
          <header class="modal__head">
            <div class="modal__title">
              <span class="modal__unit">{{ selectedUnit.unit_short_id }}</span>
              <span class="modal__plates" v-if="selectedUnit.unit_plates">{{ selectedUnit.unit_plates }}</span>
              <span class="modal__state" :data-state="selectedUnit.state">{{ stateLabel(selectedUnit.state) }}</span>
            </div>
            <button class="modal__close" aria-label="Cerrar" @click="closeUnit">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7l1.4-1.4 6.3 6.3 6.3-6.3Z" fill="currentColor" transform="translate(1.4 0)"/></svg>
            </button>
          </header>

          <nav class="modal__tabs">
            <button
              class="tab"
              :class="{ 'tab--active': activeTab === 'historial' }"
              @click="activeTab = 'historial'"
            >Historial</button>
            <button
              class="tab"
              :class="{ 'tab--active': activeTab === 'ubicacion' }"
              @click="activeTab = 'ubicacion'"
            >Ubicación</button>
          </nav>

          <section class="modal__body">
            <!-- Historial -->
            <div v-if="activeTab === 'historial'" class="history">
              <p class="history__caption">Servicios de los últimos 7 días</p>
              <div v-if="historyLoading" class="modal__hint">Cargando historial…</div>
              <div v-else-if="historyError" class="modal__hint modal__hint--error">{{ historyError }}</div>
              <div v-else-if="!history.length" class="modal__hint">Sin servicios en la última semana.</div>
              <div v-else class="history__list">
                <component
                  :is="svc.note_ident ? 'a' : 'article'"
                  v-for="(svc, i) in history"
                  :key="i"
                  class="history-row"
                  :class="{ 'history-row--link': svc.note_ident }"
                  v-bind="svc.note_ident ? {
                    href: noteUrl(svc.note_ident),
                    target: '_blank',
                    rel: 'noopener',
                    title: 'Abrir OT ' + svc.note_number + ' en otra pestaña',
                  } : {}"
                >
                  <div class="history-row__top">
                    <span class="service-chip">
                      {{ svc.note_number ? 'OT ' + svc.note_number : (svc.kind === 'montacargas' ? 'Montacargas' : 'Servicio') }}
                    </span>
                    <span class="history-row__status" v-if="svc.note_status">{{ svc.note_status }}</span>
                    <svg v-if="svc.note_ident" class="service-row__ext" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7ZM19 19H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7h-2v7Z" fill="currentColor"/></svg>
                    <span class="history-row__when">{{ elapsedLabel(svc.start_time || svc.assigned_at) }}</span>
                  </div>
                  <div class="history-row__meta">
                    <span v-if="svc.client_name">{{ svc.client_name }}</span>
                    <span v-if="svc.service_type" class="history-row__dot-sep">{{ svc.service_type }}</span>
                  </div>
                  <div class="history-row__meta history-row__meta--muted">
                    <span>{{ svc.operator_name || 'Sin operador' }}</span>
                    <span class="history-row__times">
                      {{ fmtDateTime(svc.start_time || svc.assigned_at) }}
                      <template v-if="svc.end_time"> → {{ fmtDateTime(svc.end_time) }}</template>
                      <template v-else> · en curso</template>
                    </span>
                  </div>
                </component>
              </div>
            </div>

            <!-- Ubicación -->
            <div v-else class="location">
              <div v-if="locationLoading && !unitPosition" class="modal__hint">Buscando ubicación…</div>
              <div v-else-if="!unitPosition" class="modal__hint">
                Sin ubicación disponible para esta unidad.
                <span v-if="locationError" class="modal__hint--error"><br />{{ locationError }}</span>
              </div>
              <template v-else>
                <div class="location__meta">
                  <span class="location__chip">
                    <svg viewBox="0 0 24 24" width="13" height="13"><path d="M13 4.07V2h-2v2.07A8 8 0 0 0 4.07 11H2v2h2.07A8 8 0 0 0 11 19.93V22h2v-2.07A8 8 0 0 0 19.93 13H22v-2h-2.07A8 8 0 0 0 13 4.07ZM12 18a6 6 0 1 1 6-6 6 6 0 0 1-6 6Zm0-9a3 3 0 1 0 3 3 3 3 0 0 0-3-3Z" fill="currentColor"/></svg>
                    {{ vehicleSpeed }} km/h
                  </span>
                  <span class="location__coords">{{ unitPosition.lat.toFixed(5) }}, {{ unitPosition.lng.toFixed(5) }}</span>
                </div>
                <GMap :center="unitPosition" :zoom="15" map-type-id="roadmap" class="location__map">
                  <Marker :position="unitPosition" />
                </GMap>
              </template>
            </div>
          </section>
        </div>
      </div>
    </transition>

    <!-- Configuration modal: choose which units are visible -->
    <transition name="uo-modal">
      <div v-if="showConfig" class="uo-modal-backdrop" @click.self="closeConfig">
        <div class="uo-modal uo-modal--config" role="dialog" aria-modal="true" aria-label="Configuración del panel de unidades">
          <header class="modal__head">
            <div class="modal__title">
              <span class="modal__unit">Configuración</span>
              <span class="modal__plates">Unidades visibles en el panel</span>
            </div>
            <button class="modal__close" aria-label="Cerrar" @click="closeConfig">
              <svg viewBox="0 0 24 24" width="18" height="18"><path d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7l1.4-1.4 6.3 6.3 6.3-6.3Z" fill="currentColor" transform="translate(1.4 0)"/></svg>
            </button>
          </header>

          <div class="config-controls">
            <div class="search config-controls__search">
              <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 4.99L20.49 19Zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14Z" fill="currentColor"/></svg>
              <input v-model.trim="configSearch" type="search" placeholder="Filtrar unidades…" />
            </div>
            <div class="config-controls__actions">
              <button class="btn btn--ghost btn--sm" @click="configDraft = units.map(u => u.id)">Todas</button>
              <button class="btn btn--ghost btn--sm" @click="configDraft = []">Ninguna</button>
              <span class="config-controls__count">{{ configDraft.length }} / {{ units.length }} visibles</span>
            </div>
          </div>

          <section class="modal__body config-body">
            <table class="config-table">
              <thead>
                <tr>
                  <th class="config-table__check">Visible</th>
                  <th>Unidad</th>
                  <th>Categoría</th>
                  <th>Placas</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="u in configUnits" :key="u.id">
                  <td class="config-table__check">
                    <input
                      type="checkbox"
                      :checked="configDraft.includes(u.id)"
                      @change="toggleConfigUnit(u.id, $event.target.checked)"
                    />
                  </td>
                  <td class="config-table__id">{{ u.unit_short_id }}</td>
                  <td>{{ u.unit_category || '—' }}</td>
                  <td>{{ u.unit_plates || '—' }}</td>
                  <td>{{ stateLabel(u.state) }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="!configUnits.length" class="modal__hint">Sin coincidencias</div>
          </section>

          <footer class="config-foot">
            <span v-if="configError" class="config-foot__error">{{ configError }}</span>
            <button class="btn btn--ghost" @click="closeConfig">Cancelar</button>
            <button class="btn btn--primary" :disabled="configSaving" @click="saveConfig">
              {{ configSaving ? 'Guardando…' : 'Guardar' }}
            </button>
          </footer>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import axios from 'axios'
import { Map as GMap, Marker } from '@fawmi/vue-google-maps'
import brand from '@/branding'
import { useDashboardWS } from '@/composables/useDashboardWS'

const STALE_HOURS = 24

const units = ref([])
const loading = ref(true)
const error = ref(null)
const now = ref(Date.now())
const searchQuery = ref('')
const sortBy = ref('inactividad')
const activeBoard = ref('gruas')
const selectedCategories = ref([])
const permissions = ref([])

// Visibility config (which units the dashboard shows)
const visibleUnitIds = ref(null) // null = not configured, show everything
const showConfig = ref(false)
const configDraft = ref([])
const configSearch = ref('')
const configSaving = ref(false)
const configError = ref(null)

const MONTACARGAS_CATEGORY = 'MONTACARGAS'
// Preferred chip order; anything else found in data gets appended
const CATEGORY_ORDER = ['A', 'B', 'C', 'D', 'AUXILIO VIAL', 'NO APLICA']

const ws = useDashboardWS('operations')
const wsStatus = computed(() => ws.status.value)

function apiBase() {
  return process.env.API_DIRECTORY || ''
}

function authHeaders() {
  return { Authorization: 'Token ' + localStorage.getItem('authToken') }
}

function hasPerm(perm) {
  return permissions.value.includes(perm) || permissions.value.includes(`access_control.${perm}`)
}

const canConfigure = computed(() => hasPerm('ui_access_units_overview_config'))

async function fetchPermissions() {
  try {
    const { data } = await axios.get(`${apiBase()}/current-user/`, { headers: authHeaders() })
    permissions.value = data.permissions || []
  } catch (e) {
    console.error('current-user fetch error', e)
  }
}

async function fetchConfig() {
  try {
    const { data } = await axios.get(`${apiBase()}/dashboard/units-overview-config`, {
      headers: authHeaders(),
    })
    visibleUnitIds.value = data.is_configured ? new Set(data.visible_unit_ids || []) : null
  } catch (e) {
    console.error('units-overview-config fetch error', e)
  }
}

async function fetchOverview() {
  loading.value = true
  error.value = null
  try {
    const { data } = await axios.get(`${apiBase()}/dashboard/units-overview`, {
      headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
    })
    units.value = data.results || []
    // Keep the open modal pointing at the fresh copy of the unit
    if (selectedUnit.value) {
      const fresh = units.value.find((u) => u.id === selectedUnit.value.id)
      if (fresh) selectedUnit.value = fresh
    }
  } catch (e) {
    console.error('units-overview fetch error', e)
    error.value = 'No se pudo cargar el estado de las unidades.'
  } finally {
    loading.value = false
  }
}

function hoursSince(iso) {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return null
  return (now.value - t) / 3600000
}

function isStale(unit) {
  const h = hoursSince(unit.last_service_at)
  return h !== null && h >= STALE_HOURS
}

function isMaintenanceStale(unit) {
  const h = hoursSince(unit.maintenance_since)
  return h !== null && h >= STALE_HOURS
}

function elapsedLabel(iso) {
  const h = hoursSince(iso)
  if (h === null) return ''
  if (h < 1) return `hace ${Math.max(1, Math.round(h * 60))} min`
  if (h < 48) return `hace ${Math.round(h)} h`
  const days = Math.floor(h / 24)
  const rest = Math.round(h % 24)
  return rest ? `hace ${days} d ${rest} h` : `hace ${days} d`
}

function byUnitNumber(a, b) {
  return String(a.unit_short_id).localeCompare(String(b.unit_short_id), undefined, { numeric: true })
}

function sortedLibre(list) {
  // Stalest units first so the ones needing attention surface at the top
  return [...list].sort((a, b) => {
    const ha = hoursSince(a.last_service_at)
    const hb = hoursSince(b.last_service_at)
    if (ha === null && hb === null) return byUnitNumber(a, b)
    if (ha === null) return 1
    if (hb === null) return -1
    return hb - ha
  })
}

function matchesSearch(unit, query) {
  const haystack = [
    unit.unit_short_id,
    unit.unit_plates,
    unit.operator_name,
    ...unit.services.map((s) => s.note_number),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return haystack.includes(query)
}

function sortUnits(list, colKey) {
  if (sortBy.value === 'unidad') return [...list].sort(byUnitNumber)
  if (sortBy.value === 'operador') {
    return [...list].sort((a, b) => {
      if (!a.operator_name && !b.operator_name) return byUnitNumber(a, b)
      if (!a.operator_name) return 1
      if (!b.operator_name) return -1
      return a.operator_name.localeCompare(b.operator_name)
    })
  }
  // Default "inactividad": stalest-first where last service is shown, rest by unit number
  return (colKey === 'libre' || colKey === 'fuera') ? sortedLibre(list) : [...list].sort(byUnitNumber)
}

function isMontacargas(unit) {
  return (unit.unit_category || '').toUpperCase() === MONTACARGAS_CATEGORY
}

function isVisible(unit) {
  return visibleUnitIds.value === null || visibleUnitIds.value.has(unit.id)
}

// Units surviving the config visibility filter, split by board
const visibleUnits = computed(() => units.value.filter(isVisible))

const boardCounts = computed(() => {
  let gruas = 0
  let montacargas = 0
  for (const u of visibleUnits.value) {
    if (isMontacargas(u)) montacargas += 1
    else gruas += 1
  }
  return { gruas, montacargas }
})

const categoryOptions = computed(() => {
  const found = new Set()
  for (const u of visibleUnits.value) {
    if (!isMontacargas(u) && u.unit_category) found.add(u.unit_category.toUpperCase())
  }
  const ordered = CATEGORY_ORDER.filter((c) => found.has(c))
  const extras = [...found].filter((c) => !CATEGORY_ORDER.includes(c)).sort()
  return [...ordered, ...extras]
})

function toggleCategory(cat) {
  if (selectedCategories.value.includes(cat)) {
    selectedCategories.value = selectedCategories.value.filter((c) => c !== cat)
  } else {
    selectedCategories.value = [...selectedCategories.value, cat]
  }
}

const columns = computed(() => {
  const query = searchQuery.value.toLowerCase()
  const wantMontacargas = activeBoard.value === 'montacargas'
  const cats = selectedCategories.value
  const byState = { libre: [], en_servicio: [], fuera: [], mantenimiento: [] }

  for (const u of visibleUnits.value) {
    if (!byState[u.state]) continue
    if (isMontacargas(u) !== wantMontacargas) continue
    if (!wantMontacargas && cats.length && !cats.includes((u.unit_category || '').toUpperCase())) continue
    if (query && !matchesSearch(u, query)) continue
    byState[u.state].push(u)
  }
  return [
    { key: 'libre', title: 'Libre', units: sortUnits(byState.libre, 'libre') },
    { key: 'en_servicio', title: 'En servicio', units: sortUnits(byState.en_servicio, 'en_servicio') },
    { key: 'fuera', title: 'Fuera de servicio', units: sortUnits(byState.fuera, 'fuera') },
    { key: 'mantenimiento', title: 'Mantenimiento', units: sortUnits(byState.mantenimiento, 'mantenimiento') },
  ]
})

// Debounced refetch: several signals can fire in a burst when a note is saved
let refetchTimer = null
function scheduleRefetch() {
  clearTimeout(refetchTimer)
  refetchTimer = setTimeout(fetchOverview, 600)
}

ws.onMessage((msg) => {
  if (['active_units_changed', 'orders_or_types_changed', 'units_changed'].includes(msg.topic)) {
    scheduleRefetch()
  }
})

/* ---- Unit detail modal ---- */
const selectedUnit = ref(null)
const activeTab = ref('historial')

const history = ref([])
const historyLoading = ref(false)
const historyError = ref(null)

const vehicleLocation = ref(null)
const locationLoading = ref(false)
const locationError = ref(null)
let locationTimer = null

const STATE_LABELS = {
  libre: 'Libre',
  en_servicio: 'En servicio',
  fuera: 'Fuera de servicio',
  mantenimiento: 'Mantenimiento',
}
function stateLabel(state) {
  return STATE_LABELS[state] || state
}

const unitPosition = computed(() => {
  const loc = vehicleLocation.value
  if (!loc) return null
  const lat = Number(loc.lat)
  const lng = Number(loc.long)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
})

const vehicleSpeed = computed(() => Number(vehicleLocation.value?.vehicle_speed) || 0)

function openUnit(unit) {
  selectedUnit.value = unit
  activeTab.value = 'historial'
  vehicleLocation.value = null
  locationError.value = null
  fetchHistory(unit)
}

function closeUnit() {
  selectedUnit.value = null
  stopLocationPolling()
}

async function fetchHistory(unit) {
  historyLoading.value = true
  historyError.value = null
  history.value = []
  try {
    const { data } = await axios.get(`${apiBase()}/dashboard/unit-service-history`, {
      params: { unit_id: unit.id, days: 7 },
      headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
    })
    history.value = data.results || []
  } catch (e) {
    console.error('unit-service-history fetch error', e)
    historyError.value = 'No se pudo cargar el historial.'
  } finally {
    historyLoading.value = false
  }
}

async function fetchLocation() {
  const unit = selectedUnit.value
  if (!unit) return
  locationLoading.value = true
  try {
    // Same live-location source the other tracking components use
    const { data } = await axios.get(`${apiBase()}/fetch-last-vehicle-locations-new`)
    const rows = Array.isArray(data) ? data : []
    vehicleLocation.value = rows.find(
      (r) => String(r.vehicle_name) === String(unit.unit_short_id)
    ) || null
    locationError.value = null
  } catch (e) {
    console.error('vehicle locations fetch error', e)
    locationError.value = 'No se pudo consultar el servidor de ubicaciones.'
  } finally {
    locationLoading.value = false
  }
}

function startLocationPolling() {
  stopLocationPolling()
  fetchLocation()
  locationTimer = setInterval(fetchLocation, 20000)
}

function stopLocationPolling() {
  clearInterval(locationTimer)
  locationTimer = null
}

watch(activeTab, (tab) => {
  if (tab === 'ubicacion' && selectedUnit.value) startLocationPolling()
  else stopLocationPolling()
})

function onKeydown(e) {
  if (e.key !== 'Escape') return
  if (selectedUnit.value) closeUnit()
  else if (showConfig.value) closeConfig()
}

function noteUrl(noteIdent) {
  return '/notes/' + encodeURIComponent(String(noteIdent))
}

/* ---- Visibility configuration ---- */
const configUnits = computed(() => {
  const query = configSearch.value.toLowerCase()
  const list = [...units.value].sort(byUnitNumber)
  if (!query) return list
  return list.filter((u) =>
    [u.unit_short_id, u.unit_plates, u.unit_category]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(query)
  )
})

function openConfig() {
  if (!canConfigure.value) return
  configDraft.value = visibleUnitIds.value === null
    ? units.value.map((u) => u.id)
    : [...visibleUnitIds.value]
  configSearch.value = ''
  configError.value = null
  showConfig.value = true
}

function closeConfig() {
  showConfig.value = false
}

function toggleConfigUnit(unitId, checked) {
  if (checked) {
    if (!configDraft.value.includes(unitId)) configDraft.value = [...configDraft.value, unitId]
  } else {
    configDraft.value = configDraft.value.filter((id) => id !== unitId)
  }
}

async function saveConfig() {
  configSaving.value = true
  configError.value = null
  try {
    await axios.put(
      `${apiBase()}/dashboard/units-overview-config`,
      { visible_unit_ids: configDraft.value },
      { headers: authHeaders() }
    )
    visibleUnitIds.value = new Set(configDraft.value)
    showConfig.value = false
  } catch (e) {
    console.error('units-overview-config save error', e)
    configError.value = 'No se pudo guardar la configuración.'
  } finally {
    configSaving.value = false
  }
}

function fmtDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

let clockTimer = null
let pollTimer = null

onMounted(() => {
  fetchOverview()
  fetchPermissions()
  fetchConfig()
  clockTimer = setInterval(() => { now.value = Date.now() }, 30000)
  // Safety-net poll in case a WS message is missed
  pollTimer = setInterval(fetchOverview, 120000)
  window.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  clearTimeout(refetchTimer)
  clearInterval(clockTimer)
  clearInterval(pollTimer)
  stopLocationPolling()
  window.removeEventListener('keydown', onKeydown)
})
</script>

<style scoped>
.units-overview {
  --bg: #eef2fb;
  --panel: #ffffff;
  --text: #0f1b33;
  --muted: #5f6880;
  --line: rgba(16, 24, 40, 0.11);
  --shadow: 0 20px 40px rgba(15, 23, 42, 0.08);
  --green: #16a34a;
  --blue: #1d4ed8;
  --gray: #6b7280;
  --amber: #d97706;
  --red: #dc2626;

  min-height: 100vh;
  background:
    radial-gradient(circle at 18% -5%, rgba(255, 196, 52, 0.14), transparent 42%),
    radial-gradient(circle at 82% -12%, rgba(29, 78, 216, 0.14), transparent 40%),
    var(--bg);
  padding: 11.5vh 16px 28px;
}

.nav-bar-operations {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 10vh;
  z-index: 1000;
  display: flex;
  align-items: center;
  padding-left: 16px;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.05);
}
.logo-button { height: 55%; object-fit: contain; }

.shell { width: 100%; margin: 0 auto; }

.header {
  margin-top: 16px;
  padding: 16px 20px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(251, 253, 255, 0.98));
  box-shadow: var(--shadow);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.header__left { display: flex; align-items: center; gap: 14px; }
.icon-badge {
  width: 46px;
  height: 46px;
  border-radius: 13px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #1d4ed8, #4f7df9);
  color: #fff;
  box-shadow: 0 10px 22px rgba(29, 78, 216, 0.28);
}
.eyebrow {
  margin: 0 0 2px;
  color: var(--muted);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.header h1 { margin: 0; font-size: 1.2rem; color: var(--text); }
.header__right { display: flex; align-items: center; gap: 10px; }

.live-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 12px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--panel);
  color: var(--muted);
  font-size: 0.8rem;
  font-weight: 600;
}
.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #9ca3af;
}
.live-pill[data-live="true"] { color: #14532d; border-color: rgba(22, 163, 74, 0.35); }
.live-pill[data-live="true"] .live-dot {
  background: var(--green);
  box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.5);
  animation: livePulse 2s infinite;
}
@keyframes livePulse {
  0% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.45); }
  70% { box-shadow: 0 0 0 7px rgba(22, 163, 74, 0); }
  100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0); }
}

.btn {
  padding: 9px 14px;
  border-radius: 11px;
  border: 1px solid transparent;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  transition: transform 0.15s ease, background 0.2s ease;
}
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.btn--ghost { background: var(--panel); border-color: var(--line); color: var(--text); }
.btn--ghost:hover { background: #f5f8ff; }
.btn--primary { background: var(--blue); color: #fff; }
.btn--primary:hover { transform: translateY(-1px); box-shadow: 0 8px 18px rgba(29, 78, 216, 0.3); }
.btn--sm { padding: 6px 11px; font-size: 0.78rem; }

/* ---- Toolbar ---- */
.toolbar {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.board-tabs {
  display: inline-flex;
  padding: 4px;
  border: 1px solid var(--line);
  border-radius: 13px;
  background: var(--panel);
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.05);
  gap: 4px;
}
.board-tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--muted);
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.board-tab:hover { color: var(--text); }
.board-tab--active {
  background: var(--blue);
  color: #fff;
}
.board-tab__count {
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(15, 27, 51, 0.08);
  font-size: 0.72rem;
  font-weight: 700;
}
.board-tab--active .board-tab__count {
  background: rgba(255, 255, 255, 0.22);
}

/* ---- Category filters ---- */
.cat-filters {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
}
.cat-filters__label {
  font-size: 0.74rem;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-right: 3px;
}
.cat-chip {
  padding: 6px 13px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--panel);
  color: var(--text);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.cat-chip:hover { border-color: rgba(29, 78, 216, 0.4); }
.cat-chip--active {
  background: var(--blue);
  border-color: var(--blue);
  color: #fff;
}
.search {
  flex: 1;
  min-width: 240px;
  max-width: 420px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.05);
  color: var(--muted);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.search:focus-within {
  border-color: rgba(29, 78, 216, 0.45);
  box-shadow: 0 0 0 4px rgba(29, 78, 216, 0.12);
}
.search input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  padding: 10px 0;
  font-size: 0.86rem;
  color: var(--text);
}
.search input::placeholder { color: var(--muted); }
.search input::-webkit-search-cancel-button { display: none; }
.search__clear {
  border: none;
  background: transparent;
  color: var(--muted);
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  padding: 4px;
}
.search__clear:hover { color: var(--text); }

.sort {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: var(--muted);
  font-weight: 600;
}
.sort select {
  padding: 9px 12px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: var(--panel);
  color: var(--text);
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.05);
}
.sort select:focus {
  outline: none;
  border-color: rgba(29, 78, 216, 0.45);
  box-shadow: 0 0 0 4px rgba(29, 78, 216, 0.12);
}

/* ---- Board ----
   Libre and En servicio get the lion's share of the width; their cards
   flow into multiple narrow columns to minimize vertical scrolling. */
.board {
  margin-top: 12px;
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 3fr) minmax(0, 1.4fr) minmax(0, 1.4fr);
  gap: 12px;
  align-items: start;
}

.column {
  --accent: var(--gray);
  border: 1px solid var(--line);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: var(--shadow);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 220px;
}
.column--libre { --accent: var(--green); }
.column--en_servicio { --accent: var(--blue); }
.column--fuera { --accent: var(--gray); }
.column--mantenimiento { --accent: var(--amber); }

.column__head {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 13px 15px;
  border-bottom: 1px solid var(--line);
  background: color-mix(in srgb, var(--accent) 7%, #ffffff);
}
.column__dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: var(--accent);
}
.column__head h2 {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--text);
  flex: 1;
}
.column__count {
  min-width: 26px;
  text-align: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--accent);
  color: #fff;
  font-size: 0.78rem;
  font-weight: 700;
}

.column__body {
  padding: 9px;
  overflow-y: auto;
  max-height: calc(100vh - 36vh);
}
.column__list {
  position: relative; /* anchors absolute-positioned leaving cards */
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px;
}

.column__empty {
  text-align: center;
  color: var(--muted);
  font-size: 0.82rem;
  padding: 26px 0;
  border: 1px dashed var(--line);
  border-radius: 12px;
}

/* ---- Unit cards ---- */
.unit-card {
  border: 1px solid var(--line);
  border-left: 4px solid var(--accent);
  border-radius: 11px;
  background: var(--panel);
  padding: 8px 10px;
  display: grid;
  gap: 4px;
  align-content: start;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.unit-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.1);
}

.unit-card__top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.unit-card__id {
  font-size: 0.98rem;
  font-weight: 800;
  color: var(--text);
  letter-spacing: 0.01em;
}
.unit-card__plates {
  font-size: 0.68rem;
  color: var(--muted);
  font-weight: 600;
  text-transform: uppercase;
}

.unit-card__operator {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #37425c;
  font-size: 0.75rem;
  min-width: 0;
}
.unit-card__operator svg { color: var(--muted); flex-shrink: 0; }
.unit-card__operator span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.unit-card__services { display: grid; gap: 4px; }
.service-row {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
}
.service-chip {
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(29, 78, 216, 0.1);
  color: var(--blue);
  font-size: 0.74rem;
  font-weight: 700;
}
.service-row--link {
  text-decoration: none;
  color: inherit;
  border-radius: 8px;
  margin: -3px -5px;
  padding: 3px 5px;
  transition: background 0.15s ease;
}
.service-row--link:hover { background: rgba(29, 78, 216, 0.09); }
.service-row--link:hover .service-chip {
  background: rgba(29, 78, 216, 0.2);
  text-decoration: underline;
}
.service-row__ext {
  color: var(--muted);
  flex-shrink: 0;
}
.service-row--link:hover .service-row__ext,
.history-row--link:hover .service-row__ext { color: var(--blue); }
.service-meta { font-size: 0.74rem; color: var(--muted); }
.service-meta--time { margin-left: auto; }

.unit-card__last {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.76rem;
  color: var(--muted);
}
.unit-card__last--alert { color: var(--red); font-weight: 700; }

/* Blinking alert for free units idle > 24h */
.unit-card--alert {
  border-color: rgba(220, 38, 38, 0.55);
  border-left-color: var(--red);
  animation: blinkRed 1.3s ease-in-out infinite;
}
@keyframes blinkRed {
  0%, 100% {
    background: var(--panel);
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
  }
  50% {
    background: rgba(220, 38, 38, 0.09);
    box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.14);
  }
}

/* Card enter/leave/move animations when units switch columns */
.card-enter-active {
  transition: opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1), transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.card-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
  /* Take leaving cards out of the flow so siblings slide up smoothly */
  position: absolute;
  left: 0;
  right: 0;
  z-index: 0;
}
.card-enter-from { opacity: 0; transform: translateY(16px) scale(0.95); }
.card-leave-to { opacity: 0; transform: scale(0.92); }
.card-move { transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1); }

.error-note {
  margin-top: 14px;
  text-align: center;
  color: var(--red);
  font-weight: 600;
}

/* ---- Unit detail modal ----
   Prefixed classes: Bootstrap (global) also defines .modal / .modal-backdrop */
.uo-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(10, 16, 32, 0.45);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.uo-modal {
  width: min(640px, 100%);
  max-height: min(78vh, 720px);
  display: flex;
  flex-direction: column;
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: 18px;
  box-shadow: 0 30px 60px rgba(15, 23, 42, 0.3);
  overflow: hidden;
}

.modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 15px 18px;
  border-bottom: 1px solid var(--line);
}
.modal__title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.modal__unit {
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--text);
}
.modal__plates {
  font-size: 0.76rem;
  color: var(--muted);
  font-weight: 600;
  text-transform: uppercase;
}
.modal__state {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #fff;
  background: var(--gray);
}
.modal__state[data-state="libre"] { background: var(--green); }
.modal__state[data-state="en_servicio"] { background: var(--blue); }
.modal__state[data-state="mantenimiento"] { background: var(--amber); }

.modal__close {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: var(--panel);
  color: var(--muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.15s ease, color 0.15s ease;
}
.modal__close:hover { background: #fef2f2; color: var(--red); }

.modal__tabs {
  display: flex;
  gap: 4px;
  padding: 10px 14px 0;
  border-bottom: 1px solid var(--line);
}
.tab {
  padding: 9px 16px;
  border: none;
  background: transparent;
  color: var(--muted);
  font-weight: 700;
  font-size: 0.86rem;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  border-radius: 8px 8px 0 0;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}
.tab:hover { background: #f5f8ff; color: var(--text); }
.tab--active {
  color: var(--blue);
  border-bottom-color: var(--blue);
}

.modal__body {
  padding: 14px 18px 18px;
  overflow-y: auto;
  flex: 1;
  min-height: 300px;
}

.modal__hint {
  text-align: center;
  color: var(--muted);
  font-size: 0.86rem;
  padding: 40px 0;
}
.modal__hint--error { color: var(--red); }

/* History tab */
.history__caption {
  margin: 0 0 10px;
  color: var(--muted);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.history__list { display: grid; gap: 8px; }
.history-row {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 10px 12px;
  display: grid;
  gap: 5px;
}
.history-row--link {
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}
.history-row--link:hover {
  border-color: rgba(29, 78, 216, 0.45);
  box-shadow: 0 6px 14px rgba(29, 78, 216, 0.12);
  transform: translateY(-1px);
}
.history-row--link:hover .service-chip {
  background: rgba(29, 78, 216, 0.2);
  text-decoration: underline;
}
.history-row__top {
  display: flex;
  align-items: center;
  gap: 8px;
}
.history-row__status {
  font-size: 0.74rem;
  font-weight: 600;
  color: #37425c;
}
.history-row__when {
  margin-left: auto;
  font-size: 0.74rem;
  color: var(--muted);
}
.history-row__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.8rem;
  color: #37425c;
  flex-wrap: wrap;
}
.history-row__meta--muted { color: var(--muted); font-size: 0.76rem; }
.history-row__dot-sep::before {
  content: "·";
  margin-right: 10px;
  color: var(--muted);
}
.history-row__times { margin-left: auto; }

/* Location tab */
.location { display: grid; gap: 10px; }
.location__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.location__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: #f5f8ff;
  color: var(--text);
  font-size: 0.78rem;
  font-weight: 700;
}
.location__coords {
  font-size: 0.74rem;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
.location__map {
  width: 100%;
  height: 380px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--line);
}

/* ---- Configuration modal ---- */
.uo-modal--config { width: min(760px, 100%); }

.config-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 18px;
  border-bottom: 1px solid var(--line);
}
.config-controls__search {
  flex: 1;
  min-width: 200px;
  max-width: 300px;
}
.config-controls__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.config-controls__count {
  font-size: 0.78rem;
  color: var(--muted);
  font-weight: 600;
}

.config-body { padding: 0; }
.config-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
}
.config-table th,
.config-table td {
  padding: 8px 14px;
  border-bottom: 1px solid rgba(16, 24, 40, 0.07);
  text-align: left;
  color: var(--text);
}
.config-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f5f8ff;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.config-table__check { width: 60px; text-align: center; }
.config-table__check input { width: 16px; height: 16px; cursor: pointer; }
.config-table__id { font-weight: 700; }
.config-table tbody tr:hover { background: #f8fbff; }

.config-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 18px;
  border-top: 1px solid var(--line);
}
.config-foot__error {
  margin-right: auto;
  color: var(--red);
  font-size: 0.8rem;
  font-weight: 600;
}

/* Modal transition: backdrop fades, card springs up */
.uo-modal-enter-active { transition: opacity 0.28s ease; }
.uo-modal-leave-active { transition: opacity 0.2s ease; }
.uo-modal-enter-active .uo-modal {
  transition: transform 0.38s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.28s ease;
}
.uo-modal-leave-active .uo-modal {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.uo-modal-enter-from, .uo-modal-leave-to { opacity: 0; }
.uo-modal-enter-from .uo-modal { transform: translateY(28px) scale(0.94); opacity: 0; }
.uo-modal-leave-to .uo-modal { transform: translateY(14px) scale(0.97); opacity: 0; }

@media (max-width: 1200px) {
  .board { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .column__body { max-height: none; }
}
@media (max-width: 640px) {
  .units-overview { padding-left: 12px; padding-right: 12px; }
  .board { grid-template-columns: 1fr; }
  .header { flex-direction: column; align-items: stretch; }
  .header__right { justify-content: space-between; }
}

@media (prefers-reduced-motion: reduce) {
  .unit-card--alert { animation: none; background: rgba(220, 38, 38, 0.08); }
  .live-pill[data-live="true"] .live-dot { animation: none; }
  .card-enter-active,
  .card-leave-active,
  .card-move,
  .uo-modal-enter-active,
  .uo-modal-leave-active,
  .uo-modal-enter-active .uo-modal,
  .uo-modal-leave-active .uo-modal { transition: none; }
}
</style>
